/**
 * Shared live mark-wind fetch.
 *
 * Two consumers draw from the same data — RaceCourseLayer (per-mark wind arrows) and WindGridOverlay
 * (interpolated wind field) — on different cadences (10s vs 30s plus a fast-retry burst on enable).
 * Fetching independently meant duplicate HTTP for an identical payload and, worse, the two overlays
 * could render from snapshots taken seconds apart. This module gives them one request and one answer.
 *
 * Influx is NOT involved here: `/api/stream/markwind/live` serves the stream server's in-memory rolling
 * window, or the durable MARKWIND Parquet for ranges older than that window. The live head (no range)
 * is what keeps the server poll alive, so this must only be called while the live map is mounted.
 */

import { apiEndpoints } from "../config/env";
import { getData } from "../utils/global";
import { debug } from "../utils/console";

export interface MarkwindMark {
  NAME: string;
  LAT: string;
  LON: string;
  /** Mark's own speed (km/h) — MDSS-reported, or derived from its previous fix. */
  BSP?: string;
  TWS?: string;
  TWD?: string;
  /**
   * 3 = the mark was effectively stationary so its wind is trustworthy. The server only ever serves
   * grade-3 wind: a drifting mark carries its last grade-3 wind instead (see DELAYED/AGE_S).
   */
  GRADE?: "3" | "1";
  /** "true" when TWS/TWD are held from an earlier grade-3 sample rather than measured at this timestep. */
  DELAYED?: string;
  /** Age in seconds of the held wind; "0" when measured at this timestep. */
  AGE_S?: string;
  /**
   * Client-side only — set by `utils/markWindQuality.ts` on a mark whose TWS was too low to believe while
   * other marks in the same timestep were measuring real wind. TWS/TWD are removed from such a mark; the flag
   * exists because RaceCourseLayer's persistent-mark map holds a mark's LAST known wind when a reading goes
   * missing, so absence alone would leave the dead sensor's old arrow frozen on screen. Never sent by the
   * server.
   */
  WIND_IGNORED?: boolean;
}

export interface MarkwindTimestep {
  DATETIME: string;
  MARKS: MarkwindMark[];
}

/**
 * How long a fetched payload is reused. The server only recomputes every 10s, so a 5s TTL collapses the
 * two consumers' overlapping cadences (and WindGridOverlay's 300/900/2000/4000ms enable retries) onto a
 * single request without ever serving data older than one server poll.
 */
const TTL_MS = 5000;

interface CacheEntry {
  key: string;
  atMs: number;
  data: MarkwindTimestep[];
}

let cache: CacheEntry | null = null;
let inFlight: { key: string; promise: Promise<MarkwindTimestep[]> } | null = null;

/** Distinct cache key per requested range; the head (no range) is its own key. */
function cacheKey(startMs?: number, endMs?: number): string {
  const hasRange = Number.isFinite(startMs) && Number.isFinite(endMs);
  return hasRange ? `${Math.round(startMs as number)}-${Math.round(endMs as number)}` : "head";
}

function buildUrl(startMs?: number, endMs?: number): string {
  const base = apiEndpoints.stream.markwindLive;
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return base;
  }
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}start_ms=${Math.round(startMs as number)}&end_ms=${Math.round(endMs as number)}`;
}

/**
 * Fetch live mark-wind timesteps, shared across consumers.
 *
 * Deliberately does NOT forward the caller's AbortSignal to the request: the response is shared, so one
 * component unmounting must not cancel the fetch the other is waiting on. Callers that care should check
 * their own signal after awaiting.
 *
 * @returns timesteps ascending by DATETIME; empty array when there is no live data (caller falls back to
 *          the batch `markwind` project object).
 */
export async function fetchLiveMarkwind(opts?: {
  startMs?: number;
  endMs?: number;
  /** Bypass the TTL cache and force a fresh request (still deduped against any in-flight one). */
  force?: boolean;
}): Promise<MarkwindTimestep[]> {
  const startMs = opts?.startMs;
  const endMs = opts?.endMs;
  const key = cacheKey(startMs, endMs);
  const now = Date.now();

  if (!opts?.force && cache && cache.key === key && now - cache.atMs < TTL_MS) {
    return cache.data;
  }
  if (inFlight && inFlight.key === key) {
    return inFlight.promise;
  }

  const promise = (async (): Promise<MarkwindTimestep[]> => {
    try {
      const resp = await getData(buildUrl(startMs, endMs));
      if (resp?.type === "AbortError") {
        return [];
      }
      if (resp?.success && Array.isArray(resp.data)) {
        const data = resp.data as MarkwindTimestep[];
        cache = { key, atMs: Date.now(), data };
        return data;
      }
      return [];
    } catch (e) {
      debug("[markwindService] live fetch failed", e);
      return [];
    } finally {
      if (inFlight && inFlight.key === key) {
        inFlight = null;
      }
    }
  })();

  inFlight = { key, promise };
  return promise;
}

/** Drop the cached payload — call when the live context changes (project/class/date, streaming restart). */
export function invalidateLiveMarkwind(): void {
  cache = null;
}
